import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, Form, Button, Table, Card } from 'antd';
import { generateExportFileName, toLocalTime } from 'utils';
import { getRangeDate, setRangeDate, clearRangeDate } from 'utils/dateRangeStorage';
import { FormProps } from 'rc-field-form';
import 'styles/folio-revenue-report.scss';
import { useGetFolioRevenueReport } from 'hooks/useGetFolioRevenueReport';
import { getColumnFolioRevenueReport } from 'constants/table';
import { PAGE_SIZE_FOLIO_REVENUE_REPORT } from 'services/api/module/report.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import ExportFolioRevenueReport from 'components/common/export-folio-revenue-report';
import { useTranslation } from 'react-i18next';
import IBButton from 'helper/permission.action';
import { PermissionActionTypes } from 'helper/type/action.type';
import useModal from 'stores/useModal';
import { useSelectMultiBranches } from 'hooks/useSelectMultiBranches';
import SelectMultiBranchesModal from 'components/common/select-multi-branches-modal';
import set from 'lodash/set';

const initStartDate = dayjs().startOf('day');

function FolioRevenueReport() {
  const { t } = useTranslation();
  const { isOpenSourceBranchesModal, setIsOpenSourceBranchesModal } = useModal();
  const [form] = Form.useForm();

  const [isInitialized, setIsInitialized] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_FOLIO_REVENUE_REPORT);
  const [filterOptions, setFilterOptions] = useState<any>();

  const {
    selectedSourceBranches,
    setSelectedSourceBranches,
    setAppliedSourceBranches,
    hasSourceBranchesPermission,
    getBranchIdsString
  } = useSelectMultiBranches(PermissionActionTypes.FOLIO_REVENUE_REPORT_BRANCHES);

  const {
    data: folioData,
    pagination,
    isFetching
  } = useGetFolioRevenueReport(filterOptions, page, pageSize);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_FOLIO_REVENUE_REPORT, page, filterOptions]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  useEffect(() => {
    const savedRange = getRangeDate();
    if (savedRange) {
      const startDate = dayjs(savedRange.startDate);
      const endDate = dayjs(savedRange.endDate);
      form.setFieldsValue({
        rangeDate: [startDate, endDate]
      });
      setPage(1);
      setPageSize(PAGE_SIZE_FOLIO_REVENUE_REPORT);
      setFilterOptions({
        check_in: toLocalTime(String(startDate), 'YYYY-MM-DD'),
        check_out: toLocalTime(String(endDate), 'YYYY-MM-DD')
      });
    } else {
      form.setFieldsValue({
        rangeDate: [initStartDate, initStartDate.add(1, 'day')]
      });
      setPage(1);
      setPageSize(PAGE_SIZE_FOLIO_REVENUE_REPORT);
      setFilterOptions({
        check_in: toLocalTime(String(initStartDate), 'YYYY-MM-DD'),
        check_out: toLocalTime(String(initStartDate.add(1, 'day')), 'YYYY-MM-DD')
      });
    }
    setIsInitialized(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearRangeDate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        set(restFilterOptions, 'check_in', toLocalTime(String(startDate), 'YYYY-MM-DD'));
        set(restFilterOptions, 'check_out', toLocalTime(String(endDate), 'YYYY-MM-DD'));
        const dayCount = endDate.diff(startDate, 'day');
        setRangeDate(startDate, endDate, dayCount + 1);
      }
    }

    if (hasSourceBranchesPermission) {
      const branchIds = getBranchIdsString();
      if (branchIds) {
        set(restFilterOptions, 'branch_ids', branchIds);
      }
    }

    if (page !== 1) {
      setPage(1);
    }
    setFilterOptions(restFilterOptions);
  };

  const handleRangeDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const dayCount = end.diff(start, 'day');
      setRangeDate(start, end, dayCount + 1);
    }
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const columnFolioRevenueReport = useMemo(() => {
    return getColumnFolioRevenueReport(t);
  }, [t]);

  if (!isInitialized) return null;

  return (
    <div className="pms-folio-revenue-report">
      <Card title={t('managePage.folioRevenueReport.title')}>
        <div className="pms-folio-revenue-report__timetable">
          <Form
            form={form}
            initialValues={{
              rangeDate: [initStartDate, initStartDate.add(1, 'day')]
            }}
            onFinish={onFinish}
            autoComplete="off"
          >
            <div className="pms-folio-revenue-report__filter">
              <div className="pms-folio-revenue-report__filter__left">
                <Form.Item name="rangeDate">
                  <DatePicker.RangePicker
                    className="w-full"
                    placeholder={[
                      t('managePage.folioRevenueReport.invoiceDateFrom'),
                      t('managePage.folioRevenueReport.invoiceDateTo')
                    ]}
                    format="DD-MM-YYYY"
                    onChange={handleRangeDateChange}
                  />
                </Form.Item>
                <Form.Item>
                  <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                    {t('common.actions.submitButton')}
                  </Button>
                </Form.Item>
                <Form.Item>
                  <IBButton
                    key="ok"
                    permission={PermissionActionTypes.FOLIO_REVENUE_REPORT_BRANCHES}
                    className="ant-btn-secondary"
                    onClick={() => setIsOpenSourceBranchesModal(true)}
                  >
                    {t('common.selectMultipleBranches')}
                  </IBButton>
                </Form.Item>
              </div>
              <ExportFolioRevenueReport
                fileName={generateExportFileName('folio-revenue-report')}
                filterOptions={filterOptions}
                t={t}
              />
            </div>
          </Form>
          <div className="pms-folio-revenue-report__table">
            <Table
              loading={isFetching}
              rowKey={record =>
                record.booking_line_order_id != null
                  ? String(record.booking_line_order_id)
                  : 'TOTAL'
              }
              columns={columnFolioRevenueReport}
              dataSource={folioData}
              bordered
              rowClassName={record => (String(record.sale_order_ref) === 'TOTAL' ? 'bold-row' : '')}
              pagination={{
                current: page,
                position: ['bottomCenter'],
                total: pagination.total,
                pageSize: pageSize,
                locale: { items_per_page: `/ ${t('common.page')}` },
                showTotal: total => `${total} ${t('common.item')}`,
                onChange: (page: number) => handleChangePage(page),
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                }
              }}
              locale={{
                emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
              }}
              scroll={{ x: 1500, y: 500 }}
            />
          </div>
        </div>
      </Card>

      <SelectMultiBranchesModal
        open={isOpenSourceBranchesModal}
        onCancel={() => setIsOpenSourceBranchesModal(false)}
        onOk={() => {
          setAppliedSourceBranches(selectedSourceBranches);
          setIsOpenSourceBranchesModal(false);
        }}
        selectedSourceBranches={selectedSourceBranches}
        setSelectedSourceBranches={setSelectedSourceBranches}
      />
    </div>
  );
}

export default FolioRevenueReport;
